import React, { useState } from 'react';
import { useData } from '../../store/DataContext';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import { Encounter, Patient, Vitals } from '../../types';
import { Activity, ArrowRight, History } from 'lucide-react';
export const NursingDashboard: React.FC = () => {
  const {
    encounters,
    patients,
    updateEncounter,
    currentUser,
    getPatientEncounters
  } = useData();
  const [selectedEncounter, setSelectedEncounter] = useState<Encounter | null>(null);
  // Vitals form state
  const [vitals, setVitals] = useState<Partial<Vitals>>({
    temp: '',
    pulse: '',
    resp: '',
    bp: '',
    sp02: '',
    weight: '',
    height: '',
    bmi: '',
    painScore: '',
    avpu: 'Alert'
  });
  const pendingEncounters = encounters.filter(e => e.stage === 'NURSING');
  const getPatient = (patientId: string) => patients.find(p => p.id === patientId);
  const handleSelectEncounter = (encounter: Encounter) => {
    setSelectedEncounter(encounter);
    setVitals({
      temp: '',
      pulse: '',
      resp: '',
      bp: '',
      sp02: '',
      weight: '',
      height: '',
      bmi: '',
      painScore: '',
      avpu: 'Alert'
    });
  };
  const calculateBMI = (weightStr: string, heightStr: string) => {
    const weight = parseFloat(weightStr);
    const heightCm = parseFloat(heightStr);
    if (!isNaN(weight) && !isNaN(heightCm) && heightCm > 0) {
      const heightM = heightCm / 100;
      const bmi = (weight / (heightM * heightM)).toFixed(1);
      setVitals(prev => ({
        ...prev,
        weight: weightStr,
        height: heightStr,
        bmi
      }));
    } else {
      setVitals(prev => ({
        ...prev,
        weight: weightStr,
        height: heightStr,
        bmi: ''
      }));
    }
  };
  const handleSaveVitals = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedEncounter || !currentUser) return;
    const completeVitals: Vitals = {
      ...(vitals as Vitals),
      recordedAt: new Date().toISOString(),
      recordedBy: currentUser.id
    };
    updateEncounter(selectedEncounter.id, {
      vitals: completeVitals,
      stage: 'CLINICIAN'
    });
    alert('Vitals saved. Patient forwarded to Clinician.');
    setSelectedEncounter(null);
  };
  if (selectedEncounter) {
    const patient = getPatient(selectedEncounter.patientId);
    if (!patient) return <div data-id="element-972">Patient not found</div>;
    const pastEncounters = getPatientEncounters(patient.id).filter(e => e.id !== selectedEncounter.id && e.vitals);
    return <div className="space-y-6" data-id="element-973">
        <div className="flex justify-between items-center" data-id="element-974">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-975">
            Nursing Assessment
          </h1>
          <Button variant="outline" onClick={() => setSelectedEncounter(null)} data-id="element-976">
            Back to Queue
          </Button>
        </div>

        <Card className="bg-blue-50 border-blue-200" data-id="element-977">
          <div className="flex justify-between items-start" data-id="element-978">
            <div data-id="element-979">
              <h2 className="text-xl font-bold text-gray-900" data-id="element-980">
                {patient.firstName} {patient.lastName}
              </h2>
              <p className="text-sm text-gray-600" data-id="element-981">
                BMC: {patient.bmcFileNumber} • {patient.age} yrs • {patient.sex}
              </p>
            </div>
            <div className="text-right" data-id="element-982">
              <p className="text-sm font-medium text-red-600" data-id="element-983">
                Allergies: {patient.allergies}
              </p>
              <p className="text-sm text-gray-600" data-id="element-984">
                HIV Status: {patient.hivStatus}
              </p>
            </div>
          </div>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-985">
          <div className="lg:col-span-2" data-id="element-986">
            <Card title="Enter Vitals" data-id="element-987">
              <form onSubmit={handleSaveVitals} className="space-y-6" data-id="element-988">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-id="element-989">
                  <Input label="Blood Pressure (mmHg)" placeholder="120/80" required value={vitals.bp} onChange={e => setVitals({
                  ...vitals,
                  bp: e.target.value
                })} data-id="element-990" />
                  <Input label="Temperature (°C)" type="number" step="0.1" required value={vitals.temp} onChange={e => setVitals({
                  ...vitals,
                  temp: e.target.value
                })} data-id="element-991" />
                  <Input label="Pulse (bpm)" type="number" required value={vitals.pulse} onChange={e => setVitals({
                  ...vitals,
                  pulse: e.target.value
                })} data-id="element-992" />
                  <Input label="Resp. Rate (cpm)" type="number" required value={vitals.resp} onChange={e => setVitals({
                  ...vitals,
                  resp: e.target.value
                })} data-id="element-993" />
                  <Input label="SpO2 (%)" type="number" required value={vitals.sp02} onChange={e => setVitals({
                  ...vitals,
                  sp02: e.target.value
                })} data-id="element-994" />
                  <Input label="Pain Score (0-10)" type="number" min="0" max="10" required value={vitals.painScore} onChange={e => setVitals({
                  ...vitals,
                  painScore: e.target.value
                })} data-id="element-995" />

                  <Input label="Weight (kg)" type="number" step="0.1" required value={vitals.weight} onChange={e => calculateBMI(e.target.value, vitals.height || '')} data-id="element-996" />
                  <Input label="Height (cm)" type="number" step="0.1" required value={vitals.height} onChange={e => calculateBMI(vitals.weight || '', e.target.value)} data-id="element-997" />
                  <Input label="BMI" readOnly value={vitals.bmi} className="bg-gray-50" data-id="element-998" />
                </div>

                <div className="border-t border-gray-200 pt-4" data-id="element-999">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1000">
                    AVPU Scale
                  </label>
                  <div className="flex space-x-4" data-id="element-1001">
                    {['Alert', 'Verbal', 'Pain', 'Unresponsive'].map(level => <label key={level} className="flex items-center" data-id="element-1002">
                          <input type="radio" name="avpu" value={level} checked={vitals.avpu === level} onChange={e => setVitals({
                      ...vitals,
                      avpu: e.target.value
                    })} className="mr-2" data-id="element-1003" />
                          {level}
                        </label>)}
                  </div>
                </div>

                <div className="flex justify-end pt-4" data-id="element-1004">
                  <Button type="submit" size="lg" data-id="element-1005">
                    Save & Forward to Clinician
                  </Button>
                </div>
              </form>
            </Card>
          </div>

          <div className="lg:col-span-1" data-id="element-1006">
            <Card title="Vitals History" className="h-full" data-id="element-1007">
              {pastEncounters.length > 0 ? <div className="space-y-4 overflow-y-auto max-h-[500px] pr-2" data-id="element-1008">
                  {pastEncounters.map(enc => <div key={enc.id} className="bg-gray-50 p-3 rounded border border-gray-200 text-sm" data-id="element-1009">
                      <p className="font-medium text-gray-900 mb-2 border-b pb-1" data-id="element-1010">
                        {new Date(enc.startedAt).toLocaleDateString()}
                      </p>
                      <div className="grid grid-cols-2 gap-2" data-id="element-1011">
                        <div data-id="element-1012">
                          <span className="text-gray-500" data-id="element-1013">BP:</span>{' '}
                          {enc.vitals?.bp}
                        </div>
                        <div data-id="element-1014">
                          <span className="text-gray-500" data-id="element-1015">Temp:</span>{' '}
                          {enc.vitals?.temp}°C
                        </div>
                        <div data-id="element-1016">
                          <span className="text-gray-500" data-id="element-1017">Pulse:</span>{' '}
                          {enc.vitals?.pulse}
                        </div>
                        <div data-id="element-1018">
                          <span className="text-gray-500" data-id="element-1019">SpO2:</span>{' '}
                          {enc.vitals?.sp02}%
                        </div>
                        <div data-id="element-1020">
                          <span className="text-gray-500" data-id="element-1021">Weight:</span>{' '}
                          {enc.vitals?.weight}kg
                        </div>
                        <div data-id="element-1022">
                          <span className="text-gray-500" data-id="element-1023">BMI:</span>{' '}
                          {enc.vitals?.bmi}
                        </div>
                      </div>
                    </div>)}
                </div> : <div className="text-center py-8 text-gray-500 flex flex-col items-center" data-id="element-1024">
                  <History className="w-8 h-8 mb-2 text-gray-400" data-id="element-1025" />
                  <p data-id="element-1026">No previous vitals recorded</p>
                </div>}
            </Card>
          </div>
        </div>
      </div>;
  }
  return <div className="space-y-6" data-id="element-1027">
      <div className="flex justify-between items-center" data-id="element-1028">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-1029">Nursing Queue</h1>
        <Badge variant="info" className="text-sm px-3 py-1" data-id="element-1030">
          {pendingEncounters.length} Patients Waiting
        </Badge>
      </div>

      <Card noPadding data-id="element-1031">
        <ul className="divide-y divide-gray-200" data-id="element-1032">
          {pendingEncounters.length > 0 ? pendingEncounters.map(encounter => {
          const patient = getPatient(encounter.patientId);
          if (!patient) return null;
          const waitTime = Math.floor((new Date().getTime() - new Date(encounter.startedAt).getTime()) / 60000);
          return <li key={encounter.id} className="p-4 hover:bg-gray-50 flex justify-between items-center" data-id="element-1033">
                  <div className="flex items-center" data-id="element-1034">
                    <div className="bg-blue-100 p-2 rounded-full mr-4" data-id="element-1035">
                      <Activity className="h-6 w-6 text-blue-600" data-id="element-1036" />
                    </div>
                    <div data-id="element-1037">
                      <p className="text-sm font-medium text-blue-600" data-id="element-1038">
                        {patient.bmcFileNumber}
                      </p>
                      <p className="text-lg font-bold text-gray-900" data-id="element-1039">
                        {patient.firstName} {patient.lastName}
                      </p>
                      <p className="text-sm text-gray-500" data-id="element-1040">
                        Waiting for: {waitTime} mins
                      </p>
                    </div>
                  </div>
                  <Button onClick={() => handleSelectEncounter(encounter)} data-id="element-1041">
                    Assess Patient <ArrowRight className="w-4 h-4 ml-2" data-id="element-1042" />
                  </Button>
                </li>;
        }) : <li className="p-12 text-center text-gray-500" data-id="element-1043">
              <Activity className="mx-auto h-12 w-12 text-gray-300 mb-4" data-id="element-1044" />
              <p className="text-lg font-medium text-gray-900" data-id="element-1045">
                Queue is empty
              </p>
              <p data-id="element-1046">No patients are currently waiting for nursing assessment.</p>
            </li>}
        </ul>
      </Card>
    </div>;
};